import { validateOrgNumberForCountry } from 'App/services/prospectingService';
import type { LookupQueryKind } from 'App/shadcn/pages/ProspectingTools/Lookup/lookupApi';

/**
 * What Lookup would read a supersearch query as, so supersearch can offer to look it up. Mirrors
 * `detectQuery` in base/src/core/services/prospectingAgent/lookup/detectQuery.ts (same kinds, same
 * order), which the UI cannot import; the server still decides, this only picks the row's wording.
 *
 * One deliberate difference: a LinkedIn profile may carry anything after its slug (a language
 * segment like `/en`, a tracking path), which the server reads too once WEB-13096 lands.
 */

const LINKEDIN_PROFILE = /^(?:https?:\/\/)?(?:[a-z]{2,3}\.)?linkedin\.com\/in\/[^/?#\s]+(?:[/?#]\S*)?$/i;
// Any page of a company's LinkedIn profile (WEB-13378).
const LINKEDIN_COMPANY = /^(?:https?:\/\/)?(?:[a-z]{2,3}\.)?linkedin\.com\/company\/[^/?#\s]+(?:[/?#]\S*)?$/i;
const EMAIL = /^[^\s@,<>]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}$/i;
const WEBSITE = /^(?:https?:\/\/)?(?:www\.)?((?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,})(?::\d+)?(?:[/?#]\S*)?$/i;

export const lookupQueryKind = (raw: string): LookupQueryKind => {
	const query = raw.trim();
	if (!query) return 'unknown';
	if (LINKEDIN_PROFILE.test(query)) return 'linkedinUrl';
	if (LINKEDIN_COMPANY.test(query)) return 'linkedinCompany';
	if (EMAIL.test(query)) return 'email';
	if (validateOrgNumberForCountry('SE', query)) return 'orgNumber';

	const website = WEBSITE.exec(query);
	if (website) {
		const domain = website[1].toLowerCase();
		// Any other LinkedIn page (a company, a post) is not a website of the company it shows.
		return domain === 'linkedin.com' || domain.endsWith('.linkedin.com') ? 'unknown' : 'domain';
	}

	const comma = query.indexOf(',');
	if (comma > 0) {
		const name = query.slice(0, comma).trim();
		const companyName = query.slice(comma + 1).trim();
		if (/\p{L}/u.test(name) && !/[\d@]/.test(name) && companyName.length >= 2) return 'nameAndCompany';
	}

	return 'unknown';
};

/** The kinds that look up a person (and may spend AI credits); the rest look up a company. */
export const isPersonKind = (kind: LookupQueryKind) =>
	kind === 'linkedinUrl' || kind === 'email' || kind === 'nameAndCompany';
